<script setup lang="ts">
/**
 * 多 Tab 栏。
 *
 * <h2>实现</h2>
 * 用 naive-ui 的 `n-tabs`（`type="card"` + 可关闭）。tab 数据全在 `useTabsStore`，
 * 这里只负责**展示 + 把用户操作回灌给 store / router**：
 *  - 点 tab → `update:value` → `router.push`；成功路由同步激活态
 *  - 当前页点 × → 相邻路由通过离开确认 → `store.close` 驱逐原缓存
 *
 * <h2>⚠️ 不在这里持路由状态</h2>
 * 当前激活 tab 来自 `store.activeKey`，而它由 `AppShell` 里的 `watch($route)` 维护。
 * ⇒ 这一层是「受控」的，⛔ 不自己猜当前是哪个 tab。
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { NTabs, NTab } from 'naive-ui'
import { useTabsStore } from '@/stores/tabs'

const store = useTabsStore()
const router = useRouter()
const navigationPending = ref(false)

/** 🔀 运营配置组页签：标题显示「组 · 当前分区」（如「服务接入 · 短信服务商」）。
 * 分区名取自该页签最后停留 fullPath 的 ?tab（syncFromRoute 已随路由变化更新）。 */
const CONFIG_GROUP_TAB_NAMES: Record<string, Record<string, string>> = {
  '/ops/config/access': { sms: '短信服务商', payment: '支付商户', logistics: '快递聚合商', ai: 'AI 模型' },
  '/ops/config/fees': { service: '服务费率', shipping: '运费预估', commission: '平台佣金' },
  '/ops/config/system': { features: '功能开放', data: '数据与防护' },
}
const CONFIG_GROUP_DEFAULT_TAB: Record<string, string> = {
  '/ops/config/access': 'sms', '/ops/config/fees': 'service', '/ops/config/system': 'features',
}
function tabTitleOf(tab: { key: string; title: string; fullPath: string }): string {
  const names = CONFIG_GROUP_TAB_NAMES[tab.key]
  if (!names) return tab.title
  const query = tab.fullPath.split('?')[1] ?? ''
  const tabKey = new URLSearchParams(query).get('tab') ?? CONFIG_GROUP_DEFAULT_TAB[tab.key] ?? ''
  return names[tabKey] ? `${tab.title} · ${names[tabKey]}` : tab.title
}

const value = computed({
  get: () => store.activeKey,
  set: (key: string) => {
    const target = store.tabs.find((t) => t.key === key)
    if (!target || navigationPending.value) return
    navigationPending.value = true
    void router.push(target.fullPath).catch(() => undefined).finally(() => {
      navigationPending.value = false
    })
  },
})

/** Only the active page can run its leave guard; prune its cache after navigation succeeds. */
async function onClose(name: string | number) {
  const key = String(name)
  if (navigationPending.value || store.isPersistent(key) || key !== store.activeKey) return
  const index = store.tabs.findIndex((tab) => tab.key === key)
  const closing = store.tabs[index]
  const next = store.tabs[index + 1] ?? store.tabs[index - 1]
  if (!closing || !next) return
  navigationPending.value = true
  try {
    const failure = await router.push(next.fullPath)
    if (!failure && router.currentRoute.value.fullPath === next.fullPath && store.tabs.includes(closing)) store.close(key)
  } catch {
    // Failed navigation keeps the original tab and cached form.
  } finally {
    navigationPending.value = false
  }
}
</script>

<template>
  <div class="tabbar-wrap">
    <n-tabs
      v-model:value="value"
      type="card"
      :tabs-padding="8"
      size="small"
      @close="onClose"
      class="tabbar"
    >
      <n-tab
        v-for="t in store.tabs"
        :key="t.key"
        :name="t.key"
        :tab="tabTitleOf(t)"
        :closable="!store.isPersistent(t.key)"
        :title="!store.isPersistent(t.key) && t.key !== store.activeKey ? '切换到此页后可关闭' : undefined"
      />
    </n-tabs>


  </div>
</template>

<style scoped>
/* Page tabs use the existing store and route guards. */
.tabbar-wrap {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.tabbar {
  flex: 1 1 auto;
  min-width: 0;
  --n-tab-padding: 2px 8px;
  --n-tab-font-weight-active: 600;
  --n-tab-text-color-active: var(--ink-800);
  --n-tab-text-color-hover: var(--text);
  --n-tab-color: var(--surface);
  --n-tab-border-color: var(--line);
}
.tabbar { padding:2px; border-radius:8px; background:#f1f3f7; }
.tabbar :deep(.n-tabs-tab) { height:26px; min-height:26px; box-sizing:border-box; margin:0 2px; padding:2px 8px !important; border:1px solid transparent !important; border-radius:6px !important; font-size:10px; font-weight:500; line-height:16px; }
.tabbar :deep(.n-tabs-tab:not(.n-tabs-tab--active) .n-base-close) { visibility: hidden; }
.tabbar :deep(.n-tabs-tab:not(.n-tabs-tab--active):hover .n-base-close) { visibility: visible; }

/* Page navigation uses a soft selection; filled color is reserved for actions.
   Override Naive's more specific card rule, which otherwise stays transparent. */
.tabbar :deep(.n-tabs-tab.n-tabs-tab--active) {
  background-color: #fff !important;
  color: #0a6bff !important;
  border-color: transparent !important;
  box-shadow: 0 1px 4px rgba(24,34,51,.1);
}
.tabbar :deep(.n-tabs-tab.n-tabs-tab--active .n-base-close) {
  color: var(--text-2);
}

/* 可见图标仍保持轻量，但按钮本身满足触屏 44px 目标。 */
.tabbar :deep(.n-base-close) {
  width: 44px;
  height: 44px;
  margin: -9px -12px -9px 0;
  opacity: 1;
}

</style>
